<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { isMyEasybellCom } from '@/utils/environment'
import {
  getMyEasybellComUpgradeUrl,
  redirectUpsellToSubscriptionManagement,
} from '@/utils/urls'
import { useFeaturesStore } from '@/stores/features/store'
import SlideshowModal, {
  type Step,
} from '@/components/Base/Modal/SlideshowModal/SlideshowModal.vue'
import PlanChangeModal from '@/components/PlanChangeModal.vue'
import CfcImage from '@/assets/upselling/cfc.jpg'
import AdvancedQueueImage from '@/assets/upselling/advancedQueue.jpg'
import IvrImage from '@/assets/upselling/ivr.jpg'
import CfcSwitchesImage from '@/assets/upselling/cfcSwitches.jpg'
import AnalyticsGraphsImage from '@/assets/upselling/analyticsGraphs.jpg'
import AnalyticsReportsImage from '@/assets/upselling/analyticsReports.jpg'
import DesktopAppImage from '@/assets/upselling/desktopApp.jpg'
import LiveWallboardImage from '@/assets/upselling/liveWallboard.jpg'

const emit = defineEmits<{
  (e: 'close'): void
}>()

const i18n = useI18n()
const featuresStore = useFeaturesStore()

const visibleModal = ref<'teaser' | 'plan-change'>('teaser')

const myEasybellComUpgradeUrl = computed(() => getMyEasybellComUpgradeUrl())

const teaserSteps = computed(() => {
  const upgradeButton: Step['button'] = {
    text: i18n.t(`upselling.teaser.upgradeButtonText`),
    onClick: onTeaserUpgradeButtonCLicked,
  }

  const steps: Step[] = [
    // The Call Flow Creator
    {
      image: CfcImage,
      title: i18n.t(`upselling.teaser.cfc.title`),
      text: i18n.t(`upselling.teaser.cfc.text`),
      button: upgradeButton,
    },
    // Advanced Queue configuration
    {
      image: AdvancedQueueImage,
      title: i18n.t(`upselling.teaser.advancedQueue.title`),
      text: i18n.t(`upselling.teaser.advancedQueue.text`),
      button: upgradeButton,
    },
    // IVR possibilities
    {
      image: IvrImage,
      title: i18n.t(`upselling.teaser.ivr.title`),
      text: i18n.t(`upselling.teaser.ivr.text`),
      button: upgradeButton,
    },
    // Switches in the CFC
    {
      image: CfcSwitchesImage,
      title: i18n.t(`upselling.teaser.cfcSwitches.title`),
      text: i18n.t(`upselling.teaser.cfcSwitches.text`),
      button: upgradeButton,
    },
    // Desktop App
    {
      image: DesktopAppImage,
      title: i18n.t(`upselling.teaser.desktopApp.title`),
      text: i18n.t(`upselling.teaser.desktopApp.text`),
      button: upgradeButton,
    },
    // Live Wallboards
    {
      image: LiveWallboardImage,
      title: i18n.t(`upselling.teaser.liveWallboard.title`),
      text: i18n.t(`upselling.teaser.liveWallboard.text`),
      button: upgradeButton,
    },
    // Analytics Reports
    {
      image: AnalyticsGraphsImage,
      title: i18n.t(`upselling.teaser.analyticsReport.title`),
      text: i18n.t(`upselling.teaser.analyticsReport.text`),
      button: upgradeButton,
    },
    // Analytics Wallboards
    {
      image: AnalyticsReportsImage,
      title: i18n.t(`upselling.teaser.analyticsWallboards.title`),
      text: i18n.t(`upselling.teaser.analyticsWallboards.text`),
      button: upgradeButton,
    },
  ]

  return steps
})

function onTeaserUpgradeButtonCLicked() {
  if (featuresStore.hasSubscriptionManagementFeature) {
    redirectUpsellToSubscriptionManagement()
  } else if (isMyEasybellCom()) {
    goToEasybellComUpgradePage()
  } else {
    openPlanChangeModal()
  }
}

function goToEasybellComUpgradePage() {
  window.location.href = myEasybellComUpgradeUrl.value
}

function openPlanChangeModal() {
  visibleModal.value = 'plan-change'
}
</script>

<template>
  <div>
    <SlideshowModal
      v-if="visibleModal === 'teaser'"
      :steps="teaserSteps"
      :auto-slide-millis="5000"
      show-close-button
      data-test="upselling-teaser-modal"
      @close="emit('close')"
    />

    <PlanChangeModal
      v-if="visibleModal === 'plan-change'"
      @close="emit('close')"
      :preselect-edition="'pro'"
    />
  </div>
</template>
